/**
 * Google Analytics event tracking utilities
 */

/**
 * Track a user login event in Google Analytics
 * 
 * @param {Object} options - Login tracking options
 * @param {string} options.userId - Anonymized user ID (if available)
 * @param {string} options.method - Login method (email, social, etc.)
 * @param {Object} options.additionalParams - Any additional parameters to track
 */
export const trackLogin = (options = {}) => {
  const {
    userId = 'anonymous',
    method = 'standard',
    additionalParams = {}
  } = options;

  // Get device info
  const deviceType = /Mobi|Android|iPad|iPhone|iPod/.test(navigator.userAgent) 
    ? 'mobile' 
    : 'desktop';
  
  // Get screen info
  const screenSize = `${window.screen.width}x${window.screen.height}`;
  
  // Get browser info
  const browserInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform
  };

  // Get referrer if available
  const referrer = document.referrer || 'direct';

  // User timing
  const timestamp = new Date().toISOString();
  
  // Create event parameters
  const eventParams = {
    event_category: 'user_engagement',
    event_label: `login_${method}`,
    userId,
    method,
    deviceType,
    screenSize,
    browser: browserInfo.userAgent,
    language: browserInfo.language,
    platform: browserInfo.platform,
    referrer,
    timestamp,
    ...additionalParams
  };

  // Send to Google Analytics
  if (window.gtag) {
    window.gtag('event', 'login', eventParams);
    console.log('Login event tracked:', eventParams);
  } else if (window.ga) {
    // Legacy GA tracking
    window.ga('send', {
      hitType: 'event',
      eventCategory: 'Authentication',
      eventAction: 'Login',
      eventLabel: method,
      dimension1: userId,
      dimension2: deviceType
    });
    console.log('Login event tracked with legacy GA');
  } else {
    console.warn('Google Analytics not loaded - event not tracked');
  }

  return eventParams; // Return for debugging or other uses
};

/**
 * Track a user logout event
 */
export const trackLogout = (userId = 'anonymous') => {
  if (window.gtag) {
    window.gtag('event', 'logout', {
      event_category: 'user_engagement',
      userId
    });
  }
};

/**
 * Track a site entrance event in Google Analytics
 * This captures useful information when a user enters your site
 */
export const trackSiteEntrance = () => {
  try {
    // Get device info
    const deviceType = /Mobi|Android|iPad|iPhone|iPod/.test(navigator.userAgent) 
      ? 'mobile' 
      : 'desktop';
    
    // Get screen info
    const screenSize = `${window.screen.width}x${window.screen.height}`;
    
    // Get browser info
    const browserInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform
    };

    // Get page info
    const pagePath = window.location.pathname;
    const pageTitle = document.title;
    
    // Get referrer if available
    const referrer = document.referrer || 'direct';

    // User timing
    const timestamp = new Date().toISOString();
    
    // Create event parameters
    const eventParams = {
      event_category: 'user_engagement',
      event_label: 'site_entrance',
      device_type: deviceType,
      screen_size: screenSize,
      browser: browserInfo.userAgent.substring(0, 100), // Truncate to avoid size limit
      language: browserInfo.language,
      platform: browserInfo.platform,
      page_path: pagePath,
      page_title: pageTitle,
      referrer: referrer,
      timestamp: timestamp
    };

    // Send to Google Analytics
    if (window.gtag) {
      // Send a custom event to GA4
      window.gtag('event', 'site_entrance', eventParams);
      
      // Also send a standard pageview for good measure
      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pagePath
      });
      
      console.log('✅ Site entrance tracked successfully', eventParams);
      return true;
    } 
    // Legacy Universal Analytics tracking
    else if (window.ga) {
      // Send pageview
      window.ga('send', {
        hitType: 'pageview',
        page: pagePath,
        title: pageTitle
      });
      
      // Also send a custom event
      window.ga('send', {
        hitType: 'event',
        eventCategory: 'User Engagement',
        eventAction: 'Site Entrance',
        eventLabel: deviceType
      });
      
      console.log('✅ Site entrance tracked with legacy GA');
      return true;
    } else {
      console.warn('⚠️ Google Analytics not loaded - event not tracked');
      return false;
    }
  } catch (error) {
    console.error('❌ Error tracking site entrance:', error);
    return false;
  }
};

/**
 * Track any custom event
 */
export const trackEvent = (eventName, params = {}) => {
  try {
    if (window.gtag) {
      window.gtag('event', eventName, {
        ...params,
        event_time: new Date().toISOString(),
        event_source: 'website'
      });
      console.log(`Event tracked: ${eventName}`, params);
      return true;
    } else if (window.ga) {
      window.ga('send', {
        hitType: 'event',
        eventCategory: params.category || 'general',
        eventAction: eventName,
        eventLabel: params.label || ''
      });
      console.log(`Legacy event tracked: ${eventName}`);
      return true;
    } else {
      console.warn('Google Analytics not loaded - custom event not tracked');
      return false;
    }
  } catch (error) {
    console.error('Error tracking event:', error);
    return false;
  }
};

export default {
  trackLogin,
  trackLogout,
  trackSiteEntrance,
  trackEvent
}; 